/* eslint-disable complexity */
import { DataSource, Organisation } from '@futrli-sdk/organisations';
import { FormattedMessage } from '@futrli/localisation';
import {
  getProductUrl,
  INTERCOM_LAUNCHER_ID,
  isInConnectSuccess,
  isInInitialSyncing,
  isInSyncFailure,
  isInSyncInitialFailure,
  isInSyncInitialSuccess,
  isInSyncSuccess,
  PREDICT_PRODUCT_SLUG
} from '@futrli/shared-services';
import { UISystemIcon } from '@futrli/ui-components';
import classNames from 'classnames';
import React, { useContext } from 'react';
import { useParams } from 'react-router-dom';
import { fetchOrganisationById } from '../../core/api/workspace-coordinator';
import styles from './predict-syncing-progress.scss';
import { PredictManagementContext } from '../context/predict-management-context';

const REFRESH_INTERVAL = 10000;

/**
 * Syncing progress page
 * @returns {JSX.Element}
 */
export const PredictSyncingProgress = () => {
  const { refetchOrganisation } = useContext(PredictManagementContext);
  const [fetchedOrg, setFetchedOrg] = React.useState<Organisation>();

  const [isDatasourceThroughApiSynced, setIsDatasourceThroughApiSynced] = React.useState(false);
  const [hasDatasourceThroughApiSyncFailed, setHasDatasourceThroughApiSyncFailed] = React.useState(false);

  const { organisationId: tempOrganisationId, workspaceId: tempWorkspaceId } = useParams();

  const organisationId = tempOrganisationId || '';
  const workspaceId = tempWorkspaceId || '';

  React.useEffect(() => {
    const fetchOrg = async () => {
      const newOrg = await fetchOrganisationById(workspaceId, organisationId);
      setFetchedOrg(newOrg);
    };

    const id = setInterval(() => {
      fetchOrg();
    }, REFRESH_INTERVAL);

    return () => {
      refetchOrganisation?.();
      if (id) {
        clearInterval(id);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (fetchedOrg) {
      setIsDatasourceThroughApiSynced(
        isInSyncSuccess(fetchedOrg.datasource) || isInSyncInitialSuccess(fetchedOrg.datasource)
      );

      setHasDatasourceThroughApiSyncFailed(
        isInSyncFailure(fetchedOrg.datasource) || isInSyncInitialFailure(fetchedOrg.datasource)
      );
    }
  }, [fetchedOrg]);

  React.useEffect(() => {
    if (isDatasourceThroughApiSynced) {
      window.location.href = `${window.location.origin}${getProductUrl(
        workspaceId,
        organisationId,
        PREDICT_PRODUCT_SLUG
      )}`;
    }
  }, [isDatasourceThroughApiSynced, organisationId, workspaceId]);

  return (
    <React.Fragment>
      <div className={styles.container}>
        <div className={styles.title} style={{ marginBottom: 12 }}>
          <FormattedMessage id="common.nearly_there" defaultMessage="Nearly there!" />
        </div>
        <div className={styles.iconContainer}>
          <UISystemIcon iconName="initial-sync" />
        </div>
        {!hasDatasourceThroughApiSyncFailed && !isDatasourceThroughApiSynced && (
          <div className={styles.title} style={{ marginBottom: 12, textAlign: 'center' }}>
            <FormattedMessage id="predict.we_are_pulling_your_data" defaultMessage="We’re pulling in your data..." />
          </div>
        )}

        <div style={{ textAlign: 'center' }}>
          <FormattedMessage
            id="predict.we_are_pulling_your_data_desc"
            defaultMessage="This usually takes a couple of minutes. But if you need to get on with your day, we’ll email you when Futrli is ready to go."
          />
        </div>
        {hasDatasourceThroughApiSyncFailed && (
          <p className={classNames('uiTypeFont14RegularLight', styles.error)}>
            <FormattedMessage
              id="syncing_progress.sync_failure_contact_support"
              defaultMessage={`
                      Whoops… Something’s gone wrong!
                      Your data hasn't arrived yet. But fear not, our lovely Support team would love to help you get up and running.
                    `}
            />
            <FormattedMessage
              id="syncing_progress.click_to_send_us_a_message"
              defaultMessage="Just click {HIPERLINK} to send us a message."
              values={{
                HIPERLINK: (
                  <a id={INTERCOM_LAUNCHER_ID}>
                    <FormattedMessage id="common.here" defaultMessage="here" />
                  </a>
                )
              }}
            />
          </p>
        )}
      </div>
    </React.Fragment>
  );
};

export const shouldShowSyncingProgressPage = (connectedDatasource?: DataSource): boolean => {
  if (!connectedDatasource) {
    return false;
  }

  return (
    isInInitialSyncing(connectedDatasource) ||
    isInConnectSuccess(connectedDatasource) ||
    isInSyncInitialFailure(connectedDatasource)
  );
};
